import logger from './logger';

/**
 * Verifies a reCAPTCHA token by making a request to the reCAPTCHA verification endpoint
 * @param token The reCAPTCHA token to verify
 * @returns A promise that resolves to true if the token is valid, false otherwise
 */
export async function verifyRecaptchaToken(token: string): Promise<boolean> {
  try {
    // In a production environment, this verification should happen on the server side
    // Using a proxy endpoint to avoid exposing the secret key
    // This is a client-side implementation for demo purposes
    const verificationEndpoint = '/api/verify-recaptcha';
    
    logger.info(`Attempting to verify reCAPTCHA token with endpoint: ${verificationEndpoint}`);
    
    const response = await fetch(verificationEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });
    
    if (!response.ok) {
      logger.error(`reCAPTCHA verification request failed with status: ${response.status}`, { 
        statusText: response.statusText 
      });
      return false;
    }
    
    const data = await response.json();
    
    if (data.success) {
      logger.info('reCAPTCHA verification succeeded', {
        score: data.score,
        action: data.action,
      });
      return true;
    } else {
      logger.warn('reCAPTCHA verification failed', {
        'error-codes': data['error-codes'],
      });
      return false;
    }
  } catch (error) {
    logger.error('Error verifying reCAPTCHA token', error);
    return false;
  }
}

/**
 * Gets the reCAPTCHA verification URL with the token and secret key
 * Note: This should only be used on the server side
 * @param token The reCAPTCHA token to verify
 * @returns The verification URL
 */
export function getRecaptchaVerificationUrl(token: string): string {
  // Check both formats of the secret key (with VITE_ prefix for local and without for production)
  const secretKey = import.meta.env.RECAPTCHA_SECRET_KEY || import.meta.env.VITE_RECAPTCHA_SECRET_KEY;
  
  if (!secretKey) {
    logger.warn('reCAPTCHA secret key is missing in environment variables');
  }
  
  return `https://www.google.com/recaptcha/api/siteverify?secret=${secretKey}&response=${token}`;
} 